/*
 * Embroidery / personalisation for FIGS garments.
 *
 * Responsibilities, deliberately narrow:
 *   - run the modal
 *   - keep the live stitch preview in step with what is typed
 *   - swap the font choices between Latin and Arabic based on the script typed
 *   - write the hidden line item properties onto the garment's form inputs
 *   - expose getAddonItems() so figs-product.liquid can add the SAR 44 line
 *
 * It does NOT add anything to the cart itself. figs-product.liquid owns both cart
 * paths — the theme's product-form submit and its own Buy it Now fetch — and calling
 * getAddonItems() from there keeps a single place responsible for adding to cart.
 */
if (!customElements.get('figs-embroidery')) {
  // U+0600–U+06FF is the main Arabic block; U+0750–U+077F and U+FB50–U+FDFF cover
  // supplements and presentation forms, which phone keyboards do emit.
  var ARABIC = /[؀-ۿݐ-ݿﭐ-﷿]/;

  class FigsEmbroidery extends HTMLElement {
    connectedCallback() {
      // Raw query, not qs(): the helper reads this.modal, which does not exist yet.
      this.modal    = this.querySelector('[data-emb-modal]');
      this.portal();

      this.confirm  = this.qs('[data-emb-confirm]');
      this.summaryEl= this.qs('[data-emb-summary]');
      this.priceEl  = this.qs('[data-emb-price]');
      this.removeBtn= this.qs('[data-emb-remove]');
      this.stitch   = this.qs('[data-emb-stitch]');

      this.variantId = parseInt(this.dataset.textVariant, 10);
      /* Cents, straight from the add-on products via the Liquid. Never literals:
         the charge at checkout is the variant price, so a number typed in here
         would be a second source of truth that drifts the moment someone edits
         a price in admin. */
      this.cents = {
        text: parseInt(this.dataset.textPrice, 10) || 0,
        icon: parseInt(this.dataset.iconPrice, 10) || 0,
        logo: parseInt(this.dataset.logoPrice, 10) || 0
      };

      this.state = { line1:'', line2:'', position:'left', colour:'white', font:'Block', icon:'', initials:'', applied:false };
      this.applied = { text:false, icon:false, logo:false };

      /* Remember the resting labels now, while they are still the resting
         labels. The code used to reach for a `data-idle` attribute that no
         element carries, so every "restore the idle text" call silently
         restored the CURRENT text — i.e. did nothing, leaving a stale name or
         filename on screen after a reset. */
      this.idleSummary = this.summaryEl ? this.summaryEl.textContent : '';
      this.idlePrice   = this.priceEl ? this.priceEl.textContent : '';

      this.setUpNav();
      this.setUpIcon();
      this.setUpLogo();
      this.guardEmptyLogoField();

      this.qs('[data-emb-open]').addEventListener('click', () => this.open());
      this.qs('[data-emb-close]').addEventListener('click', () => this.close());
      this.qs('[data-emb-reset]').addEventListener('click', () => this.reset());
      this.confirm.addEventListener('click', () => this.apply());
      this.removeBtn.addEventListener('click', () => this.reset(true));

      this.on('input', (e) => {
        var line = e.target.dataset.embLine;
        if (!line) return;
        this.state['line' + line] = e.target.value;
        var c = this.qs('[data-emb-count="' + line + '"]');
        if (c) c.textContent = e.target.value.length + '/26';
        this.syncScript();
        this.render();
      });

      this.on('click', (e) => {
        var key = e.target.dataset.embSet;
        if (!key) return;
        this.state[key] = e.target.dataset.value;
        var group = e.target.parentElement;
        group.querySelectorAll('[data-emb-set]').forEach((b) => b.classList.toggle('is-active', b === e.target));
        this.render();
      });

      // Escape closes, like any dialog.
      this.escHandler = (e) => { if (e.key === 'Escape' && !this.modal.hidden) this.close(); };
      document.addEventListener('keydown', this.escHandler);

      this.render();
    }

    /* PORTAL — the modal cannot stay where the snippet renders it.
     *
     * THE BUG: on sections/main-product.liquid the modal opened full-screen but was
     * painted UNDER the site header and the product gallery. Its close button sat
     * beneath the logo, and hit-testing showed the nav links were the topmost
     * elements over the open modal — so the header was clickable through it.
     *
     * THE CAUSE, measured rather than assumed. The modal is inside
     * .sticky-column-desktop, which is `position: sticky`. Unlike relative/absolute,
     * **sticky establishes a stacking context on its own** — it does not need a
     * z-index to do it. Everything inside is therefore stacked as a unit at the
     * column's level, and the modal's z-index only ever competes with its own
     * siblings. Two experiments confirmed this and ruled out the alternatives:
     *   - stripping the column's z-index changed nothing (sticky alone is enough);
     *   - raising the modal to z-index 2147483647 changed nothing (it is not a
     *     z-index competition at all);
     *   - moving the modal to <body> fixed it immediately, hit tests included.
     * It is not a containing-block problem either: the modal measured 0,0 at full
     * viewport size throughout, and offsetParent was null. Only the paint order
     * was wrong.
     *
     * So the only real fix is to leave the subtree. Escaping a stacking context
     * from the inside is not possible by any z-index value.
     *
     * WHAT MOVING COSTS, and how it is paid for below:
     *   - Delegated listeners bound to this element stop seeing modal events
     *     -> on() binds to both.
     *   - this.querySelector no longer reaches modal content -> qs()/qsa() search both.
     *   - CSS custom properties no longer inherit down -> syncFabric sets them on both.
     *   - The logo file input leaves the <form> and would stop being submitted
     *     -> re-associated below with the HTML form attribute, which works from
     *        anywhere in the document.
     */
    portal() {
      if (!this.modal) return;

      // Read the owning form BEFORE moving, while this element is still inside it.
      var form = this.closest('form');
      if (form) {
        // getAttribute, NOT form.id. A form's named controls are exposed as properties
        // on the form object, and this form contains <input name="id"> for the variant
        // — so form.id returns that INPUT, not the id attribute. Reading it here once
        // stamped the literal string "[object HTMLInputElement]" onto the association
        // below and quietly detached the upload from the form.
        var id = form.getAttribute('id');
        if (!id) {
          id = 'emb-form-' + Math.random().toString(36).slice(2, 10);
          form.setAttribute('id', id);
        }
        // Only named controls are submitted, so only they need re-associating.
        // Today that is the logo upload; this stays correct if more are added.
        this.modal.querySelectorAll('[name]').forEach(function (el) {
          el.setAttribute('form', id);
        });
      }

      document.body.appendChild(this.modal);
    }

    /* The modal now lives outside this element, so every lookup and every delegated
       listener has to consider both roots. The contains() guard keeps these correct
       if the portal ever did not happen — without it, a modal still inside this
       element would be searched twice and fire every handler twice. */
    qs(sel) {
      return this.querySelector(sel)
        || (this.modal && !this.contains(this.modal) ? this.modal.querySelector(sel) : null);
    }

    qsa(sel) {
      var found = Array.prototype.slice.call(this.querySelectorAll(sel));
      if (this.modal && !this.contains(this.modal)) {
        found = found.concat(Array.prototype.slice.call(this.modal.querySelectorAll(sel)));
      }
      return found;
    }

    on(type, fn) {
      this.addEventListener(type, fn);
      if (this.modal && !this.contains(this.modal)) this.modal.addEventListener(type, fn);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.escHandler);
      // The theme re-renders the whole product section on a variant change, which
      // disconnects this element. The portalled modal is no longer a child, so it
      // would survive that and accumulate one orphan per variant click.
      if (this.modal && this.modal.parentElement === document.body) this.modal.remove();
      document.body.style.overflow = '';
    }

    open()  { this.syncFabric(); this.modal.hidden = false; document.body.style.overflow = 'hidden'; }

    /* Tint the preview panel with the colourway the customer has actually selected, so
       the thread is previewed against the fabric they are buying rather than a guess.
       The colour is read off the live swatch element instead of being duplicated here —
       the swatch gets it from --swatch-background-color, which is set per colour in the
       section, so reading the computed value keeps one source of truth.

       TWO SELECTORS, because the two product pages render swatches differently:
         - sections/figs-product.liquid   -> .figs-pdp__swatch.is-active > span
         - sections/main-product.liquid   -> the theme's variant picker, a hidden
           radio followed by a label carrying an inline --swatch-background-color.
       Only the first existed, so on every standard product page this returned early
       and the preview fell back to the stylesheet default — a navy panel no matter
       which colour was chosen. */
    syncFabric() {
      var fabric = '';

      var sw = document.querySelector('.figs-pdp__swatch.is-active span');
      if (sw) {
        fabric = getComputedStyle(sw).backgroundColor;
      } else {
        // The label is the sibling of the checked radio; the colour lives on it as a
        // custom property, so read that first and only fall back to the painted
        // background if the property is absent.
        var lb = document.querySelector('.opt-btn:checked + .opt-label--swatch');
        if (lb) {
          var cs = getComputedStyle(lb);
          fabric = (cs.getPropertyValue('--swatch-background-color') || '').trim()
                   || cs.backgroundColor;
        }
      }

      if (!fabric) return;
      // Set on the modal as well as the host: the preview panel reads these custom
      // properties by inheritance, and portal() moved it out of this element's tree.
      this.setVar('--emb-fabric', fabric);

      // Pick a hint colour that stays readable on both a white coat and a navy top.
      //
      // The two sources use different notations — the theme's own swatches compute to
      // `rgb(27 73 100)`, while snippets/drs-swatch-styles.liquid writes plain hex —
      // so digit-scraping the raw string reads a hex like #1b4964 as garbage. Hand the
      // value to the browser and read back its normalised rgb() instead of writing a
      // parser per notation. The probe must be in the document for a computed value.
      var probe = document.createElement('span');
      probe.style.cssText = 'display:none;color:' + fabric;
      this.appendChild(probe);
      var rgb = getComputedStyle(probe).color;
      probe.remove();

      var m = rgb.match(/\d+/g);
      if (m) {
        var lum = (0.299 * m[0] + 0.587 * m[1] + 0.114 * m[2]) / 255;
        this.setVar('--emb-hint', lum > 0.6 ? '#111' : '#fff');
      }
    }

    setVar(name, value) {
      this.style.setProperty(name, value);
      if (this.modal) this.modal.style.setProperty(name, value);
    }
    close() { this.modal.hidden = true;  document.body.style.overflow = ''; }

    // Show Arabic fonts only once Arabic has actually been typed. Driven by the input,
    // not by the storefront locale: an Arabic speaker browsing the English store still
    // wants her own name stitched in Arabic.
    syncScript() {
      var isArabic = ARABIC.test(this.state.line1 + this.state.line2);
      if (isArabic === this.isArabic) return;
      this.isArabic = isArabic;

      this.qs('[data-emb-fonts="latin"]').hidden  = isArabic;
      this.qs('[data-emb-fonts="arabic"]').hidden = !isArabic;

      // The previously chosen font belongs to the other script and would be meaningless
      // on the order, so fall back to the default of whichever group is now showing.
      this.state.font = isArabic ? 'نسخ' : 'Block';
      var active = this.qs('[data-emb-fonts="' + (isArabic ? 'arabic' : 'latin') + '"] [data-emb-set="font"]');
      if (active) {
        active.parentElement.querySelectorAll('[data-emb-set="font"]').forEach((b, i) => b.classList.toggle('is-active', i === 0));
      }
    }

    render() {
      var s = this.state;
      var has = (s.line1.trim() + s.line2.trim()).length > 0;
      this.confirm.disabled = !has;

      // Live preview on the fabric panel
      this.stitch.hidden = !has;
      this.stitch.dataset.position = s.position;
      this.syncFabric();
      this.stitch.style.color = s.colour === 'black' ? '#111' : '#fff';
      this.stitch.style.fontFamily = this.isArabic
        ? "'Noto Naskh Arabic','Geeza Pro',serif"
        : (s.font === 'Script' ? "'Snell Roundhand','Brush Script MT',cursive" : "'Helvetica Neue',Arial,sans-serif");
      this.stitch.style.fontWeight = (s.font === 'Block' || s.font === 'رقعة') ? '700' : '400';
      this.stitch.dir = this.isArabic ? 'rtl' : 'ltr';
      this.qs('[data-emb-stitch1]').textContent = s.line1;
      this.qs('[data-emb-stitch2]').textContent = s.line2;

      ['position','colour','font'].forEach((k) => {
        var el = this.qs('[data-emb-echo="' + k + '"]');
        if (el) el.textContent = s[k];
      });
    }

    apply() {
      var s = this.state;
      s.applied = true;

      var summary = [
        s.line1, s.line2 ? '/ ' + s.line2 : '',
        '·', s.position === 'left' ? 'Left chest' : 'Right chest',
        '·', s.colour, '·', s.font
      ].filter(Boolean).join(' ');

      this.setProp('summary',  summary);
      this.setProp('line1',    s.line1);
      this.setProp('line2',    s.line2);
      this.setProp('position', s.position);
      this.setProp('colour',   s.colour);
      this.setProp('font',     s.font);

      this.applied.text = true;
      this.refreshRow();
      this.show('menu');
    }

    reset(alsoClose) {
      this.state = { line1:'', line2:'', position:'left', colour:'white', font:'Block',
                     icon:'', initials:'', applied:false };
      // Clear the per-option flags too. These drive getAddonItems(), so leaving
      // them set meant "Remove" wiped the personalisation but still charged for
      // it — the shopper paid SAR 44 for an empty Embroidery property.
      this.applied = { text:false, icon:false, logo:false };
      this.qsa('[data-emb-line]').forEach((i) => { i.value = ''; });
      this.qsa('[data-emb-count]').forEach((c) => { c.textContent = '0/26'; });
      this.qsa('[data-emb-prop]').forEach((i) => { i.value = ''; i.disabled = true; });
      this.summaryEl.textContent = this.idleSummary;
      if (this.priceEl) this.priceEl.textContent = this.idlePrice;
      this.removeBtn.hidden = true;
      this.isArabic = undefined;
      this.syncScript();
      this.render();
      if (alsoClose) this.close();
    }

    // Disabled inputs are not submitted, which is what keeps a plain unpersonalised
    // order free of empty embroidery properties.
    setProp(name, value) {
      var el = this.qs('[data-emb-prop="' + name + '"]');
      if (!el) return;
      el.value = value || '';
      el.disabled = !value;
    }


    /* ---- Menu navigation -------------------------------------------- */

    setUpNav() {
      this.on('click', (e) => {
        var go = e.target.closest && e.target.closest('[data-emb-goto]');
        if (go) { this.show(go.dataset.embGoto); return; }
        if (e.target.closest && e.target.closest('[data-emb-back]')) { this.show('menu'); return; }
        if (e.target.closest && e.target.closest('[data-emb-done]')) { this.close(); }
      });
    }

    show(view) {
      this.qsa('[data-emb-view]').forEach((v) => {
        v.hidden = v.dataset.embView !== view;
      });
    }

    /* ---- Icon -------------------------------------------------------- */

    setUpIcon() {
      var initials = this.qs('[data-emb-initials]');
      var confirm  = this.qs('[data-emb-confirm-icon]');
      var reset    = this.qs('[data-emb-reset-icon]');
      if (!confirm) return;

      this.on('click', (e) => {
        var b = e.target.closest && e.target.closest('[data-emb-set="icon"]');
        if (!b) return;
        this.state.icon = b.dataset.value;
        this.qsa('[data-emb-set="icon"]').forEach((x) => x.classList.toggle('is-active', x === b));
        var echo = this.qs('[data-emb-echo="icon"]');
        if (echo) echo.textContent = b.getAttribute('title') || '';
        confirm.disabled = false;
      });

      if (initials) initials.addEventListener('input', () => {
        this.state.initials = initials.value;
        var c = this.qs('[data-emb-count="i"]');
        if (c) c.textContent = initials.value.length + '/2';
      });

      confirm.addEventListener('click', () => {
        this.applied.icon = true;
        this.setProp('icon', this.state.icon);
        this.setProp('initials', this.state.initials || '');
        this.refreshRow();
        this.show('menu');
      });

      if (reset) reset.addEventListener('click', () => {
        this.applied.icon = false;
        this.state.icon = ''; this.state.initials = '';
        this.qsa('[data-emb-set="icon"]').forEach((x) => x.classList.remove('is-active'));
        if (initials) initials.value = '';
        this.setProp('icon', ''); this.setProp('initials', '');
        confirm.disabled = true;
        this.refreshRow();
      });
    }

    /* ---- Logo -------------------------------------------------------- */

    setUpLogo() {
      this.logoInput = this.qs('[data-emb-logo]');
      var confirm = this.qs('[data-emb-confirm-logo]');
      var reset   = this.qs('[data-emb-reset-logo]');
      var label   = this.qs('[data-emb-logo-label]');
      /* Captured before a filename replaces it — see the note in connectedCallback. */
      var labelIdle = label ? label.textContent : '';
      var warn    = this.qs('[data-emb-logo-warn]');
      if (!this.logoInput || !confirm) return;

      this.logoInput.addEventListener('change', () => {
        var f = this.logoInput.files && this.logoInput.files[0];
        if (!f) { confirm.disabled = true; return; }
        // Shopify rejects the upload server-side over its own limit; catching it here
        // means the customer finds out now rather than after paying.
        if (f.size > 3 * 1024 * 1024) {
          warn.hidden = false;
          this.logoInput.value = '';
          confirm.disabled = true;
          return;
        }
        warn.hidden = true;
        if (label) label.textContent = f.name;
        confirm.disabled = false;
      });

      confirm.addEventListener('click', () => {
        this.applied.logo = true;
        this.refreshRow();
        this.show('menu');
      });

      if (reset) reset.addEventListener('click', () => {
        this.applied.logo = false;
        this.logoInput.value = '';
        if (label) label.textContent = labelIdle;
        confirm.disabled = true;
        this.refreshRow();
      });
    }

    /* A logo purchase cannot use the AJAX cart: /cart/add.js takes JSON and JSON cannot
       carry a file. figs-product.liquid asks this before adding, and falls back to a
       native multipart submit when it is true. */
    needsNativeSubmit() {
      return !!(this.applied.logo && this.logoInput && this.logoInput.files.length);
    }

    /* The file input stays disabled until it is actually needed, so an ordinary text or
       icon order does not post an empty file field. */
    enableLogoField() {
      // Kept for the native-submit path in figs-product.liquid. The field is
      // enabled by default now, so this only ever needs to guarantee it.
      if (this.logoInput) this.logoInput.disabled = false;
    }

    /* An empty file input still posts an empty part, which would leave a blank
       "Logo file" property on the line. Disable it for the instant the form
       submits, then hand it straight back so the shopper can still pick a file
       after an AJAX add that did not navigate away. */
    guardEmptyLogoField() {
      var form = this.closest('form');
      if (!form || !this.logoInput) return;
      form.addEventListener('submit', () => {
        if (this.logoInput.files.length) return;
        this.logoInput.disabled = true;
        setTimeout(() => { this.logoInput.disabled = false; }, 0);
      }, true);
    }

    refreshRow() {
      var bits = [];
      var cents = 0;
      if (this.applied.text) { bits.push(this.state.line1 || this.state.line2); cents += this.cents.text; }
      if (this.applied.icon) { bits.push('Icon'); cents += this.cents.icon; }
      if (this.applied.logo) { bits.push('Logo'); cents += this.cents.logo; }

      if (bits.length) {
        this.summaryEl.textContent = bits.join(' + ');
        this.priceEl.textContent = '+' + this.dataset.currency + ' ' + this.formatCents(cents);
        this.removeBtn.hidden = false;
        return;
      }

      /* Nothing applied. This branch used to be missing entirely, so removing
         the last option left the previous name and price sitting on the trigger
         row as though embroidery were still attached. */
      this.summaryEl.textContent = this.idleSummary;
      this.priceEl.textContent = this.idlePrice;
      this.removeBtn.hidden = true;
    }

    /* Whole riyals stay whole; halalas survive if a price ever has them. */
    formatCents(cents) {
      var v = cents / 100;
      return v % 1 === 0 ? String(v) : v.toFixed(2);
    }

    /* Called by figs-product.liquid. Returns the extra cart lines this purchase needs. */
    getAddonItems() {
      var items = [];
      var forProp = this.dataset.productTitle;
      if (this.applied.text && this.variantId) {
        items.push({ id: this.variantId, quantity: 1, properties: {
          '_for': forProp, 'Embroidery': this.qs('[data-emb-prop="summary"]').value } });
      }
      if (this.applied.icon && this.dataset.iconVariant) {
        items.push({ id: parseInt(this.dataset.iconVariant, 10), quantity: 1, properties: {
          '_for': forProp, 'Embroidery': 'Icon: ' + this.state.icon + (this.state.initials ? ' (' + this.state.initials + ')' : '') } });
      }
      if (this.applied.logo && this.dataset.logoVariant) {
        items.push({ id: parseInt(this.dataset.logoVariant, 10), quantity: 1, properties: {
          '_for': forProp, 'Embroidery': 'Logo digitisation' } });
      }
      return items;
    }
  }

  customElements.define('figs-embroidery', FigsEmbroidery);
}
